import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { BarChart3, RefreshCw, Radio, Clock, Music, Megaphone, UtensilsCrossed, Star, QrCode, Loader2, X as XIcon, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminAnalyticsPage — cross-tenant KPI dashboard for superadmins.
 *
 * Replaces the defunct Telemetry tab. Pulls `/api/analytics/admin/overview`
 * which aggregates counts across every host (venue). Auto-refreshes every
 * 60 seconds to feel "live" without hammering the DB.
 *
 * Every card is clickable — opens a side sheet with the per-client
 * breakdown for that metric+range (backed by /admin/overview/breakdown).
 */
const RANGES = [
  { key: 'today',    label: 'Today' },
  { key: 'week',     label: 'This week' },
  { key: 'month',    label: 'This month' },
  { key: 'all_time', label: 'All time' },
];

const METRIC_LABELS = {
  requests: 'Song requests',
  shoutouts: 'Shoutouts',
  menu_views: 'Menu views',
  reviews: 'Reviews',
  scans: 'QR scans',
  active_clients: 'Clients playing right now',
  hours_online: 'Hours online',
};

const AdminAnalyticsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Drill-down state
  const [drill, setDrill] = useState({ open: false, metric: null, range: null, rows: [], loading: false });

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const res = await axios.get(`${API}/analytics/admin/overview`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const t = setInterval(() => fetchData(true), 60000);
    return () => clearInterval(t);
  }, [fetchData]);

  const openDrill = useCallback(async (metric, range) => {
    setDrill({ open: true, metric, range, rows: [], loading: true });
    try {
      const res = await axios.get(`${API}/analytics/admin/overview/breakdown`, {
        params: { metric, range },
        withCredentials: true,
      });
      setDrill((d) => ({ ...d, rows: res.data?.clients || [], loading: false }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load breakdown');
      setDrill((d) => ({ ...d, loading: false }));
    }
  }, []);

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 overflow-y-auto content-scroll" data-testid="admin-analytics-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <BarChart3 className="w-6 h-6 md:w-8 md:h-8 text-[#FF007F]" />
              <h1 className="text-3xl md:text-4xl font-bold text-white font-['Outfit']">Analytics</h1>
            </div>
            <p className="text-gray-400 text-sm">Live KPIs across every venue on MusicBooth. <span className="text-gray-500">Tap any card to see the per-venue breakdown.</span></p>
          </div>
          <button
            onClick={() => fetchData(true)}
            disabled={loading || refreshing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold text-gray-300 bg-white/5 border border-white/10 hover:text-white hover:bg-white/10 transition-colors"
            data-testid="admin-analytics-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {loading && !data ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 text-[#FF007F] animate-spin" />
          </div>
        ) : (
          <>
            {/* Real-time gauges */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-6" data-testid="admin-analytics-gauges">
              <GaugeCard
                icon={<Radio className="w-5 h-5" />}
                label="Clients playing right now"
                value={data?.active_clients_playing ?? 0}
                sub="Hosts with a heartbeat in the last 2 min"
                color="#39FF14"
                pulse
                testid="gauge-active-clients"
                onClick={() => openDrill('active_clients', 'today')}
              />
              <HoursOnlineCard data={data} openDrill={openDrill} />
            </div>

            {/* Counter rows */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4" data-testid="admin-analytics-counters">
              <CounterCard icon={<Music className="w-5 h-5" />}          label="Song requests"  metric="requests"    values={data?.requests}   color="#FF007F" testid="counter-requests"   openDrill={openDrill} />
              <CounterCard icon={<Megaphone className="w-5 h-5" />}      label="Shoutouts"      metric="shoutouts"   values={data?.shoutouts}  color="#9D00FF" testid="counter-shoutouts"  openDrill={openDrill} />
              <CounterCard icon={<UtensilsCrossed className="w-5 h-5" />} label="Menu views"    metric="menu_views"  values={data?.menu_views} color="#FF9500" testid="counter-menu-views" openDrill={openDrill} />
              <CounterCard icon={<Star className="w-5 h-5" />}           label="Reviews"        metric="reviews"     values={data?.reviews}    color="#FFCC00" testid="counter-reviews"    openDrill={openDrill} />
              <CounterCard icon={<QrCode className="w-5 h-5" />}         label="QR scans"       metric="scans"       values={data?.scans}      color="#00BFFF" testid="counter-scans"      openDrill={openDrill} />
            </div>

            <p className="text-gray-600 text-[10px] uppercase tracking-[0.2em] font-mono mt-6 text-right">
              Auto-refresh · 60s · last update {data?.generated_at ? new Date(data.generated_at).toLocaleTimeString() : '—'}
            </p>
          </>
        )}
      </div>

      <BreakdownSheet drill={drill} onClose={() => setDrill((d) => ({ ...d, open: false }))} />
    </AdminLayout>
  );
};


// ---------- Cards ----------------------------------------------------------

const CounterCard = ({ icon, label, metric, values, color, testid, openDrill }) => (
  <div
    className="rounded-2xl border p-4 md:p-5"
    style={{
      background: `linear-gradient(135deg, ${color}10 0%, transparent 100%)`,
      borderColor: `${color}30`,
    }}
    data-testid={testid}
  >
    <div className="flex items-center gap-2 mb-3" style={{ color }}>
      {icon}
      <p className="text-[10px] uppercase tracking-[0.2em] font-['JetBrains_Mono']">{label}</p>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
      {RANGES.map((r) => (
        <button
          type="button"
          key={r.key}
          onClick={() => openDrill(metric, r.key)}
          className="min-w-0 text-left group rounded-lg -mx-1 px-1 py-1 hover:bg-white/5 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30"
          data-testid={`${testid}-${r.key}`}
        >
          <p className="text-white text-xl md:text-2xl lg:text-3xl font-bold font-['Outfit'] leading-none tabular-nums flex items-center gap-1">
            {values?.[r.key] ?? '—'}
            <ChevronRight className="w-3.5 h-3.5 text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" />
          </p>
          <p className="text-gray-500 text-[10px] uppercase tracking-[0.15em] mt-1.5 truncate">{r.label}</p>
        </button>
      ))}
    </div>
  </div>
);

const HoursOnlineCard = ({ data, openDrill }) => (
  <div
    className="rounded-2xl border p-4 md:p-5"
    style={{
      background: 'linear-gradient(135deg, #00F5FF10 0%, transparent 100%)',
      borderColor: '#00F5FF30',
    }}
    data-testid="gauge-hours-online"
  >
    <div className="flex items-center gap-2 mb-3 text-[#00F5FF]">
      <Clock className="w-5 h-5" />
      <p className="text-[10px] uppercase tracking-[0.2em] font-['JetBrains_Mono']">Hours online</p>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
      {RANGES.map((r) => (
        <button
          type="button"
          key={r.key}
          onClick={() => openDrill('hours_online', r.key)}
          className="min-w-0 text-left group rounded-lg -mx-1 px-1 py-1 hover:bg-white/5 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30"
          data-testid={`gauge-hours-online-${r.key}`}
        >
          <p className="text-white text-xl md:text-2xl lg:text-3xl font-bold font-['Outfit'] leading-none tabular-nums flex items-center gap-1">
            {data?.[`hours_online_${r.key}`] ?? '—'}
            {data?.hours_online_estimated?.[r.key] && (
              <span className="text-[9px] font-semibold text-[#00F5FF]/70" title="Includes estimated play time from before background usage tracking was fixed">~est</span>
            )}
            <ChevronRight className="w-3.5 h-3.5 text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" />
          </p>
          <p className="text-gray-500 text-[10px] uppercase tracking-[0.15em] mt-1.5 truncate">{r.label}</p>
        </button>
      ))}
    </div>
  </div>
);

const GaugeCard = ({ icon, label, value, sub, color, pulse, testid, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="rounded-2xl border p-4 md:p-5 relative overflow-hidden text-left w-full hover:brightness-110 transition-all focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30"
    style={{
      background: `linear-gradient(135deg, ${color}15 0%, transparent 100%)`,
      borderColor: `${color}40`,
    }}
    data-testid={testid}
  >
    <div className="flex items-center gap-2 mb-3" style={{ color }}>
      {icon}
      <p className="text-[10px] uppercase tracking-[0.2em] font-['JetBrains_Mono']">{label}</p>
    </div>
    <div className="flex items-baseline gap-3">
      <p className="text-white text-5xl md:text-6xl font-extrabold font-['Outfit'] leading-none tabular-nums">
        {value}
      </p>
      {pulse && value > 0 && (
        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color }}>
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ background: color }} />
            <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: color }} />
          </span>
          LIVE
        </span>
      )}
    </div>
    <p className="text-gray-500 text-xs mt-2">{sub}</p>
  </button>
);


// ---------- Breakdown sheet ------------------------------------------------

const BreakdownSheet = ({ drill, onClose }) => {
  const label = METRIC_LABELS[drill.metric] || drill.metric || '';
  const rangeLabel = (RANGES.find((r) => r.key === drill.range) || {}).label || drill.range || '';
  const isHours = drill.metric === 'hours_online';
  const isActive = drill.metric === 'active_clients';
  const totalCount = drill.rows.reduce((s, r) => s + Number(r.count || 0), 0);

  return (
    <Sheet open={drill.open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg bg-[#0a0a0a] border-l border-white/10 text-white p-0 flex flex-col"
        data-testid="admin-analytics-breakdown-sheet"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-white/5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#FF007F] font-['JetBrains_Mono']">
                {isActive ? 'Live · right now' : rangeLabel}
              </p>
              <SheetTitle className="text-white text-xl font-['Outfit'] mt-1">{label}</SheetTitle>
              <p className="text-gray-500 text-xs mt-2">
                {drill.loading
                  ? 'Loading breakdown…'
                  : `${drill.rows.length} venue${drill.rows.length === 1 ? '' : 's'} · ${isHours ? `${totalCount.toFixed(1)} hrs` : totalCount} total`}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors"
              data-testid="admin-analytics-breakdown-close"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4 py-3" data-testid="admin-analytics-breakdown-body">
          {drill.loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-5 h-5 text-[#FF007F] animate-spin" />
            </div>
          )}
          {!drill.loading && drill.rows.length === 0 && (
            <p className="text-gray-500 text-center py-16 text-sm">No activity in this window.</p>
          )}
          {!drill.loading && drill.rows.map((row) => (
            <div
              key={row.host_id}
              className="flex items-center justify-between px-3 py-3 rounded-lg hover:bg-white/5 transition-colors"
              data-testid={`breakdown-row-${row.venue_code || row.host_id}`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm font-medium truncate">{row.venue_name || row.name || 'Unnamed venue'}</p>
                <p className="text-gray-500 text-[11px] uppercase tracking-[0.15em] font-mono truncate">
                  {row.venue_code ? row.venue_code : '—'}
                  {row.extra?.source ? ` · via ${row.extra.source}` : ''}
                </p>
              </div>
              <div className="text-right">
                <p className="text-white text-lg font-bold font-['Outfit'] tabular-nums">
                  {isHours ? `${Number(row.count).toFixed(1)}h` : Number(row.count)}
                </p>
                {isHours && row.extra?.estimated && (
                  <span
                    className="inline-block mt-0.5 text-[9px] font-semibold uppercase tracking-wider text-[#00F5FF]/80 bg-[#00F5FF]/10 rounded px-1.5 py-0.5"
                    title="Includes estimated play time from before background usage tracking was fixed"
                    data-testid={`breakdown-estimated-${row.venue_code || row.host_id}`}
                  >
                    ~ est.
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};


export default AdminAnalyticsPage;
